/**
 * ProductFundingMethodsPage — Products & Features · Product Funding Method
 * Availability. Per-(product × funding-method) admin-config matrix that drives
 * the 5-method funding picker in AddToCartModal (per Bill 2026-05-12).
 *
 * Built to match the ProductsPage CRUD pattern exactly: reload via config read,
 * a DataTable with trailing Edit/Delete ghost buttons, a top "New" Card action,
 * a ModalShell form using FormField primitives, a save() handler, and a
 * delete confirm.
 *
 * Reads:
 *   client.retail.config.productFundingMethodAvailability()  // the rows
 *   client.retail.config.products()                          // productKey FK source
 *   client.retail.config.procedureCitations()                // citationKey FK source (REQUIRED)
 *
 * Writes go through the typed bearer-only admin surface (first arg '' = no
 * bypass key — bearer-only):
 *   client.retail.admin.products.productFundingMethods.upsert('', body)
 *   client.retail.admin.products.productFundingMethods.remove('', productKey, fundingMethodKey)
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import type { ProductFundingMethodAvailabilityUpsertBody } from '@retail-ops/api-client';
import { useApiClient } from '@/contexts/ApiClientContext';
import { Banner, Button, Card, Pill, Spinner } from '@/components/primitives';
import { DataTable, type DataTableColumn } from '@/components/DataTable';
import { TextField, SelectField, BoolField, NumberField } from '@/components/FormField';
import { ModalShell } from '@/components/ModalShell';

// Row shape on the wire from /v3/config/product-funding-method-availability.
// The config read returns Record<string, unknown>; we cast to this local
// interface at load time (matches the systemErrorCodes / purgeReasonCodes
// convention noted in v3-config.ts).
interface Row {
  productKey: string;
  fundingMethodKey: string;
  behavior: string;
  displayOrder: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  defaultAmount: number | null;
  citationKey: string;
  isActive: boolean;
}

// FK source rows. /v3/config/products returns the full Product entity; we only
// read code/name here. /v3/config/procedure-citations returns
// Record<string, unknown>; we narrow the fields we display.
interface ProductRow {
  code: string;
  name: string;
}
interface CitationRow {
  citationKey: string;
  displayLabel?: string | null;
  title?: string | null;
}

// Funding-method ENUM — copied VERBATIM from the legacy editor's METHODS array
// (apps/ROP-teams/src/modules/Admin/editors/productFundingMethodAvailabilityEditor.tsx).
// Do NOT invent funding-method values.
const METHOD_OPTIONS = [
  { value: 'InternalTransfer', label: 'Internal Transfer (PREFERRED when related accts)' },
  { value: 'CashCheckInHand', label: 'Cash / Check in Hand (FSR task w/ slip)' },
  { value: 'Deferred', label: 'Deposit Deferred (deadline-tracked)' },
  { value: 'ACH', label: 'ACH External (FUTURE stub)' },
  { value: 'DebitCard', label: 'Debit Card (FUTURE stub)' },
];

// Behavior ENUM — note "Preferred" (not "Recommended") for funding.
const BEHAVIOR_OPTIONS = [
  { value: 'Unavailable', label: 'Unavailable' },
  { value: 'Available', label: 'Available' },
  { value: 'Preferred', label: 'Preferred' },
  { value: 'Required', label: 'Required' },
];

interface EditDraft {
  isNew: boolean;
  productKey: string;
  fundingMethodKey: string;
  behavior: string;
  displayOrder: number | null;
  minAmount: number | null;
  maxAmount: number | null;
  defaultAmount: number | null;
  citationKey: string;
  isActive: boolean;
}

function toNum(v: unknown): number | null {
  if (v == null) return null;
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n : null;
}

function toStr(v: unknown): string {
  return v == null ? '' : String(v);
}

// Cast a wire Record into the local Row shape.
function recordToRow(r: Record<string, unknown>): Row {
  return {
    productKey: toStr(r.productKey),
    fundingMethodKey: toStr(r.fundingMethodKey),
    behavior: toStr(r.behavior),
    displayOrder: toNum(r.displayOrder),
    minAmount: toNum(r.minAmount),
    maxAmount: toNum(r.maxAmount),
    defaultAmount: toNum(r.defaultAmount),
    citationKey: toStr(r.citationKey),
    isActive: r.isActive !== false,
  };
}

function rowToDraft(r: Row): EditDraft {
  return {
    isNew: false,
    productKey: r.productKey,
    fundingMethodKey: r.fundingMethodKey,
    behavior: r.behavior || 'Available',
    displayOrder: r.displayOrder,
    minAmount: r.minAmount,
    maxAmount: r.maxAmount,
    defaultAmount: r.defaultAmount,
    citationKey: r.citationKey,
    isActive: r.isActive,
  };
}

function blankDraft(): EditDraft {
  return {
    isNew: true,
    productKey: '',
    fundingMethodKey: 'InternalTransfer',
    behavior: 'Available',
    displayOrder: 100,
    minAmount: null,
    maxAmount: null,
    defaultAmount: null,
    citationKey: '',
    isActive: true,
  };
}

function draftToBody(d: EditDraft): ProductFundingMethodAvailabilityUpsertBody {
  return {
    productKey: d.productKey.trim(),
    fundingMethodKey: d.fundingMethodKey.trim(),
    behavior: d.behavior.trim(),
    displayOrder: d.displayOrder,
    minAmount: d.minAmount,
    maxAmount: d.maxAmount,
    defaultAmount: d.defaultAmount,
    citationKey: d.citationKey.trim(),
    isActive: d.isActive,
  };
}

export function ProductFundingMethodsPage(): JSX.Element {
  const client = useApiClient();
  const [rows, setRows] = useState<ReadonlyArray<Row> | null>(null);
  const [products, setProducts] = useState<ReadonlyArray<ProductRow>>([]);
  const [citations, setCitations] = useState<ReadonlyArray<CitationRow>>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<string>('');
  const [draft, setDraft] = useState<EditDraft | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState<string | null>(null);

  const reload = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const [rowResp, prodResp, citeResp] = await Promise.all([
        client.retail.config.productFundingMethodAvailability(),
        client.retail.config.products(),
        client.retail.config.procedureCitations(),
      ]);
      if (rowResp.success && Array.isArray(rowResp.data)) {
        setRows(rowResp.data.map(recordToRow));
      } else {
        setError(rowResp.error ?? 'Failed to load funding-method availability.');
        return;
      }
      if (prodResp.success && Array.isArray(prodResp.data)) {
        setProducts(prodResp.data as unknown as ReadonlyArray<ProductRow>);
      } else {
        setProducts([]);
      }
      if (citeResp.success && Array.isArray(citeResp.data)) {
        setCitations(
          (citeResp.data as ReadonlyArray<Record<string, unknown>>).map((c) => ({
            citationKey: toStr(c.citationKey),
            displayLabel: c.displayLabel == null ? null : String(c.displayLabel),
            title: c.title == null ? null : String(c.title),
          })),
        );
      } else {
        setCitations([]);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    }
  }, [client]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // FK dropdown options for productKey — built from the live products catalog.
  const productOptions = useMemo(
    () => products.map((p) => ({ value: p.code, label: `${p.code} — ${p.name}` })),
    [products],
  );

  // FK dropdown options for citationKey — REQUIRED FK to procedure-citations.
  // The server rejects a missing/invalid citationKey, so this is a hard
  // dropdown (no free-text). Label = `${citationKey} — ${displayLabel ?? title ?? ''}`.
  const citationOptions = useMemo(
    () =>
      citations.map((c) => ({
        value: c.citationKey,
        label: `${c.citationKey} — ${c.displayLabel ?? c.title ?? ''}`,
      })),
    [citations],
  );

  const filtered = useMemo(() => {
    if (!rows) return rows;
    if (!filter.trim()) return rows;
    const n = filter.toLowerCase();
    return rows.filter(
      (r) =>
        r.productKey.toLowerCase().includes(n) ||
        r.fundingMethodKey.toLowerCase().includes(n) ||
        r.behavior.toLowerCase().includes(n) ||
        r.citationKey.toLowerCase().includes(n),
    );
  }, [rows, filter]);

  const remove = useCallback(
    async (r: Row): Promise<void> => {
      if (
        !window.confirm(
          `Delete funding method ${r.productKey} / ${r.fundingMethodKey}? This removes the row.`,
        )
      ) {
        return;
      }
      setError(null);
      try {
        const resp = await client.retail.admin.products.productFundingMethods.remove(
          '',
          r.productKey,
          r.fundingMethodKey,
        );
        if (resp.success === false) {
          setError(resp.error ?? 'Delete failed.');
          return;
        }
        await reload();
      } catch (e) {
        setError(e instanceof Error ? e.message : String(e));
      }
    },
    [client, reload],
  );

  const columns: ReadonlyArray<DataTableColumn<Row>> = useMemo(
    () => [
      {
        header: 'Product',
        cell: (r) => <code className="cell-mono--bold">{r.productKey}</code>,
      },
      {
        header: 'Funding Method',
        cell: (r) => <code className="cell-mono">{r.fundingMethodKey}</code>,
      },
      {
        header: 'Behavior',
        cell: (r) => (
          <Pill
            tone={
              r.behavior === 'Preferred'
                ? 'ok'
                : r.behavior === 'Required'
                  ? 'warn'
                  : r.behavior === 'Unavailable'
                    ? 'muted'
                    : 'muted'
            }
          >
            {r.behavior}
          </Pill>
        ),
      },
      {
        header: 'Order',
        cell: (r) =>
          r.displayOrder != null ? (
            <span className="cell-mono">{r.displayOrder}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Min',
        cell: (r) =>
          r.minAmount != null ? (
            <span className="cell-mono">${r.minAmount}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Max',
        cell: (r) =>
          r.maxAmount != null ? (
            <span className="cell-mono">${r.maxAmount}</span>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Citation',
        cell: (r) =>
          r.citationKey ? (
            <code className="cell-mono">{r.citationKey}</code>
          ) : (
            <span className="cell-dim">—</span>
          ),
      },
      {
        header: 'Status',
        cell: (r) =>
          r.isActive ? <Pill tone="ok">active</Pill> : <Pill tone="muted">inactive</Pill>,
      },
      {
        header: '',
        cell: (r) => (
          <div className="cell-actions">
            <Button size="sm" variant="ghost" onClick={() => setDraft(rowToDraft(r))}>
              Edit
            </Button>
            <Button size="sm" variant="ghost" onClick={() => void remove(r)}>
              Delete
            </Button>
          </div>
        ),
      },
    ],
    [remove],
  );

  const save = useCallback(
    async (d: EditDraft): Promise<void> => {
      // Client-side guard mirroring the server's REQUIRED-FK contract:
      // productKey, fundingMethodKey, behavior, and citationKey are all required.
      if (!d.productKey.trim()) {
        setSaveErr('Product is required.');
        return;
      }
      if (!d.fundingMethodKey.trim()) {
        setSaveErr('Funding method is required.');
        return;
      }
      if (!d.behavior.trim()) {
        setSaveErr('Behavior is required.');
        return;
      }
      if (!d.citationKey.trim()) {
        setSaveErr('Citation is required (server rejects a missing/invalid citation).');
        return;
      }
      setSaving(true);
      setSaveErr(null);
      try {
        const body = draftToBody(d);
        const resp = await client.retail.admin.products.productFundingMethods.upsert('', body);
        if (resp.success === false) {
          setSaveErr(resp.error ?? 'Save failed.');
          return;
        }
        setDraft(null);
        await reload();
      } catch (e) {
        setSaveErr(e instanceof Error ? e.message : String(e));
      } finally {
        setSaving(false);
      }
    },
    [client, reload],
  );

  if (error) {
    return <Banner tone="err">Could not load funding-method availability: {error}</Banner>;
  }
  if (!rows) return <Spinner label="Loading funding-method availability…" />;

  return (
    <>
      <Card
        title={`Product Funding Methods — ${filtered?.length ?? 0} of ${rows.length}`}
        actions={
          <div className="users-filter">
            <TextField
              label=""
              value={filter}
              onChange={setFilter}
              placeholder="Filter by product, method, behavior, or citation…"
            />
            <Button onClick={() => setDraft(blankDraft())}>New</Button>
          </div>
        }
      >
        <DataTable<Row>
          columns={columns}
          rows={filtered ?? []}
          rowKey={(r) => `${r.productKey}::${r.fundingMethodKey}`}
          emptyState={<p className="empty">No funding methods match the current filter.</p>}
        />
      </Card>

      <ModalShell
        open={draft !== null}
        title={
          draft
            ? draft.isNew
              ? 'New funding method'
              : `Edit funding method — ${draft.productKey} / ${draft.fundingMethodKey}`
            : ''
        }
        onClose={() => (saving ? undefined : setDraft(null))}
        width={620}
        actions={
          draft ? (
            <>
              <Button variant="ghost" onClick={() => setDraft(null)} disabled={saving}>
                Cancel
              </Button>
              <Button onClick={() => void save(draft)} disabled={saving}>
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </>
          ) : null
        }
      >
        {draft && (
          <>
            {saveErr && <Banner tone="err">{saveErr}</Banner>}
            <SelectField
              label="Product"
              value={draft.productKey}
              onChange={(v) => setDraft({ ...draft, productKey: v })}
              options={productOptions}
              placeholder="— pick a product —"
              disabled={!draft.isNew}
              required
              hint={
                draft.isNew
                  ? 'FK to the Products catalog (primary-key part).'
                  : 'Read-only after create (primary-key part).'
              }
            />
            <SelectField
              label="Funding Method"
              value={draft.fundingMethodKey}
              onChange={(v) => setDraft({ ...draft, fundingMethodKey: v })}
              options={METHOD_OPTIONS}
              disabled={!draft.isNew}
              required
              hint={draft.isNew ? 'Primary-key part.' : 'Read-only after create (primary-key part).'}
            />
            <SelectField
              label="Behavior"
              value={draft.behavior}
              onChange={(v) => setDraft({ ...draft, behavior: v })}
              options={BEHAVIOR_OPTIONS}
              required
              hint="Unavailable · Available · Preferred · Required."
            />
            <SelectField
              label="Citation"
              value={draft.citationKey}
              onChange={(v) => setDraft({ ...draft, citationKey: v })}
              options={citationOptions}
              placeholder="— pick a citation —"
              required
              hint="REQUIRED FK to Procedure Citations — the server rejects a missing/invalid citation."
            />
            <NumberField
              label="Display Order"
              value={draft.displayOrder}
              onChange={(v) => setDraft({ ...draft, displayOrder: v })}
              min={0}
            />
            <NumberField
              label="Min Amount"
              value={draft.minAmount}
              onChange={(v) => setDraft({ ...draft, minAmount: v })}
              min={0}
              step={0.01}
              hint="Dollar amount (decimals allowed)."
            />
            <NumberField
              label="Max Amount"
              value={draft.maxAmount}
              onChange={(v) => setDraft({ ...draft, maxAmount: v })}
              min={0}
              step={0.01}
              hint="Dollar amount (decimals allowed)."
            />
            <NumberField
              label="Default Amount"
              value={draft.defaultAmount}
              onChange={(v) => setDraft({ ...draft, defaultAmount: v })}
              min={0}
              step={0.01}
              hint="Dollar amount (decimals allowed)."
            />
            <BoolField
              label="Active"
              value={draft.isActive}
              onChange={(v) => setDraft({ ...draft, isActive: v })}
            />
          </>
        )}
      </ModalShell>
    </>
  );
}
